// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminCategoriesList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import './AdminBlogManagement.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="abm-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
};

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  post_count: number;
}

const AdminCategoriesList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await blogAPI.adminGetCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await blogAPI.adminUpdateCategory(editingCategory.id, formData);
      } else {
        await blogAPI.adminCreateCategory(formData);
      }
      setShowForm(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this category? All posts in this category will be affected.')) {
      try {
        await blogAPI.adminDeleteCategory(id);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="abm-loading-container">
        <div className="abm-spinner">
          <Icons.Spinner />
        </div>
        <p className="abm-loading-text">Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="abm-container">
      {/* Header */}
      <div className="abm-header">
        <div className="abm-header-left">
          <h1 className="abm-title">Blog Categories</h1>
          <p className="abm-subtitle">Organize your blog posts with categories</p>
        </div>
        <div className="abm-header-actions">
          <button 
            className="abm-btn-primary"
            onClick={() => { setShowForm(true); setEditingCategory(null); setFormData({ name: '', description: '' }); }}
          >
            <Icons.Plus />
            Add Category
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="abm-stats-bar">
        <div className="abm-stat-item">
          <span className="abm-stat-label">Total Categories</span>
          <span className="abm-stat-value">{categories.length}</span>
        </div>
        <div className="abm-stat-divider"></div>
        <div className="abm-stat-item">
          <span className="abm-stat-label">Total Posts</span>
          <span className="abm-stat-value">{categories.reduce((sum, cat) => sum + cat.post_count, 0)}</span>
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="abm-modal-overlay" onClick={() => { setShowForm(false); setEditingCategory(null); }}>
          <div className="abm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="abm-modal-header">
              <h2 className="abm-modal-title">{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button className="abm-modal-close" onClick={() => { setShowForm(false); setEditingCategory(null); }}>
                <Icons.Close />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="abm-modal-form">
              <div className="abm-form-group">
                <label className="abm-form-label">Category Name *</label>
                <input
                  type="text"
                  className="abm-form-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Web Development"
                />
              </div>
              <div className="abm-form-group">
                <label className="abm-form-label">Description</label>
                <textarea
                  className="abm-form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Category description (optional)"
                  rows={3}
                />
              </div>
              <div className="abm-modal-actions">
                <button type="button" className="abm-btn-secondary" onClick={() => { setShowForm(false); setEditingCategory(null); }}>
                  Cancel
                </button>
                <button type="submit" className="abm-btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="abm-table-wrapper">
        <table className="abm-table">
          <thead>
            <tr>
              <th className="abm-col-id">ID</th>
              <th className="abm-col-name">Name</th>
              <th className="abm-col-slug">Slug</th>
              <th className="abm-col-desc">Description</th>
              <th className="abm-col-posts">Posts</th>
              <th className="abm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan={6} className="abm-empty-state">
                  <Icons.Empty />
                  <p className="abm-empty-text">No categories found</p>
                  <p className="abm-empty-subtext">Create your first category to organize your blog posts</p>
                </td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id} className="abm-table-row">
                  <td className="abm-col-id">
                    <span className="abm-id-badge">#{category.id}</span>
                  </td>
                  <td className="abm-col-name">
                    <span className="abm-category-name">{category.name}</span>
                  </td>
                  <td className="abm-col-slug">
                    <code className="abm-slug">{category.slug}</code>
                  </td>
                  <td className="abm-col-desc">
                    <span className="abm-description">{category.description || '-'}</span>
                  </td>
                  <td className="abm-col-posts">
                    <span className="abm-post-count">{category.post_count}</span>
                  </td>
                  <td className="abm-col-actions">
                    <div className="abm-actions">
                      <button 
                        className="abm-action-btn abm-action-edit"
                        onClick={() => handleEdit(category)}
                        title="Edit Category"
                      >
                        <Icons.Edit />
                      </button>
                      <button 
                        className="abm-action-btn abm-action-delete"
                        onClick={() => handleDelete(category.id)}
                        title="Delete Category"
                      >
                        <Icons.Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCategoriesList;